import { Link } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import { useBrand, isRouteHidden } from "@/config/brands";
import { useAppReviewFlags } from "@/config/appReviewFlags";
import { liveSocialLinks } from "@/config/socialLinks";
import FlagStripe from "@/components/FlagStripe";

/**
 * The real site footer — sitemap, social, provenance, legal.
 *
 * Distinct from AppFooter, which is the fixed bottom tab bar. This one sits at
 * the end of the document flow. It exists mainly so every page carries three
 * things the project's credibility depends on: where the data comes from, the
 * AMA/CPT attribution, and links to the accounts that are actually live.
 *
 * Bottom padding clears the fixed tab bar so the last row is never trapped
 * underneath it.
 */

const COLS: Array<{ heading: string; links: Array<{ to: string; label: string }> }> = [
  {
    heading: "Explore",
    links: [
      { to: "/search", label: "Search prices" },
      { to: "/scenarios", label: "Care scenarios" },
      { to: "/hospital-rankings", label: "Hospital rankings" },
      { to: "/state-comparison", label: "Compare states" },
      { to: "/international", label: "International" },
      { to: "/near-me", label: "Near me" },
    ],
  },
  {
    heading: "Learn",
    links: [
      { to: "/classroom", label: "Classroom" },
      { to: "/about/dignity", label: "Meet Dignity" },
      { to: "/data-sources", label: "Data sources" },
      { to: "/data-methodology", label: "Methodology" },
      { to: "/lawsuits", label: "Legal record" },
      { to: "/connect-to-care", label: "Connect to care" },
    ],
  },
  {
    heading: "Build",
    links: [
      { to: "/api", label: "API access" },
      { to: "/mcp", label: "MCP server" },
      { to: "/contact", label: "Contact" },
      { to: "/donate", label: "Support the work" },
    ],
  },
];

export const SiteFooter = () => {
  const { isDark } = useTheme();
  const brand = useBrand();
  const flags = useAppReviewFlags();
  const socials = liveSocialLinks();
  const year = new Date().getFullYear();

  // Only AHP has the civic palette; siblings fall back to theme tokens.
  const isAhp = brand.key === "ahp";
  const bg = isDark ? "var(--civic-bg-dark)" : "var(--civic-navy-deep)";
  const ink = "rgba(246,241,229,0.72)";
  const inkStrong = "#f6f1e5";
  const gold = "var(--civic-gold)";
  const rule = "rgba(var(--civic-gold-rgb),0.22)";
  const serif = "'Instrument Serif', Georgia, serif";
  const sans = "'Instrument Sans', system-ui, sans-serif";

  if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("embed") === "1") {
    return null;
  }

  // Sitemap columns filtered to this brand's route surface (App redirects
  // the hidden ones home — see HIDDEN_ROUTE_PREFIXES), minus columns that end
  // up empty.
  const cols = COLS.map((c) => ({
    ...c,
    links: c.links.filter((l) => {
      if (l.to === "/donate" && !flags.showPayments) return false;
      if (isRouteHidden(brand, l.to)) return false;
      return true;
    }),
  })).filter((c) => c.links.length > 0);
  // The social accounts in config are AHP's; a sibling brand shouldn't point
  // its readers at another brand's feeds.
  const showSocials = isAhp && socials.length > 0;
  const contactHref = isRouteHidden(brand, "/contact") ? `mailto:${brand.contact.support}` : "/contact";

  return (
    <footer
      style={{ background: isAhp ? bg : "hsl(var(--card))", fontFamily: sans }}
      // pb clears the fixed AppFooter tab bar (h-20 on mobile, h-16 desktop) plus safe area.
      // relative z-[1]: GlobalFlagBackground paints two position:fixed layers at z-index 0
      // (the flag video + a blend-mode wash) over everything static, which lightened this
      // navy footer into an unreadable grey (Trent, 2026-09-05). Lifting the footer one
      // level above them keeps it solid; the page content above still gets the wash.
      className="relative z-[1] mt-0 pb-28 lg:pb-24"
    >
      {/* The red/white/blue stripe is AHP's civic mark; the sibling brands
          (dark green AntiGreed, matrix-green Luigi's) get a plain rule. */}
      {isAhp ? (
        <FlagStripe height={4} />
      ) : (
        <div style={{ height: 1, background: rule }} />
      )}

      <div className="mx-auto max-w-6xl px-6 pt-12">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          {/* Identity + mission */}
          <div className="lg:pr-4">
            <div style={{ fontFamily: serif, fontSize: 22, color: inkStrong }}>
              {brand.name}
            </div>
            <p className="mt-3 text-sm leading-relaxed" style={{ color: ink }}>
              {isAhp
                ? "Every price here is published by the hospital itself, under the federal transparency rule in force since 2021. We index it so you can read it before you walk in — free, no account needed to search."
                : "Every price here is published by the hospital itself, under the federal transparency rule in force since 2021. We surface it so you can read it before you walk in — free, no account needed to search."}
            </p>

            {showSocials && (
              <div className="mt-5">
                <div
                  className="text-[11px] uppercase tracking-[0.1em] mb-3"
                  style={{ color: "rgba(246,241,229,0.45)" }}
                >
                  Follow along
                </div>
                <div className="flex items-center gap-3">
                  {socials.map((s) => (
                    <a
                      key={s.id}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${s.label} — ${s.handle}`}
                      title={`${s.label} · ${s.handle}`}
                      className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                      style={{ border: `1px solid ${rule}`, color: ink }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = gold;
                        e.currentTarget.style.borderColor = gold;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = ink;
                        e.currentTarget.style.borderColor = rule;
                      }}
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                        <path d={s.path} />
                      </svg>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sitemap columns */}
          {cols.map((col) => (
            <nav key={col.heading} aria-label={col.heading}>
              <div
                className="text-[11px] uppercase tracking-[0.1em] mb-3"
                style={{ color: gold }}
              >
                {col.heading}
              </div>
              <ul className="space-y-2">
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      className="text-sm transition-colors hover:underline"
                      style={{ color: ink }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = inkStrong)}
                      onMouseLeave={(e) => (e.currentTarget.style.color = ink)}
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        {/* Provenance + legal */}
        <div className="mt-12 pt-6" style={{ borderTop: `1px solid ${rule}` }}>
          {/* Sibling brands (Luigi's, AntiGreed) present the data but do not gather
              it — ingestion, normalization and national averages all happen on AHP.
              The provenance line used to be a separate component dropped into each
              sibling homepage; on AntiGreed it rendered inline beside the book cover
              and pushed it off-centre. It belongs here, with the rest of the
              provenance copy, on every page. */}
          {!isAhp && (
            <p className="text-xs leading-relaxed max-w-4xl mb-2" style={{ color: "rgba(246,241,229,0.6)" }}>
              Pricing data sourced from{" "}
              <a
                href="https://americanhospitalprices.com"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2"
                style={{ color: gold }}
              >
                americanhospitalprices.com
              </a>
              {" "}— compiled from each hospital's published chargemaster.
            </p>
          )}
          <p className="text-xs leading-relaxed max-w-4xl" style={{ color: "rgba(246,241,229,0.45)" }}>
            Prices come from each hospital's own published machine-readable
            chargemaster file. Figures are list prices, not what an insured patient
            necessarily pays, and they change as hospitals republish. CPT is a
            trademark of the American Medical Association; consumer-friendly names on
            this site are AI-generated from hospitals' own published chargemaster
            descriptions, <strong style={{ color: "rgba(246,241,229,0.6)" }}>not</strong>{" "}
            the AMA's official CPT descriptors. {brand.name} is not
            affiliated with or endorsed by the AMA. Nothing here is medical, legal, or
            financial advice.
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
            <span style={{ color: "rgba(246,241,229,0.4)" }}>
              © {year} {brand.name}
            </span>
            <Link to="/privacy" className="hover:underline" style={{ color: ink }}>
              Privacy
            </Link>
            <Link to="/terms" className="hover:underline" style={{ color: ink }}>
              Terms
            </Link>
            {contactHref.startsWith("mailto:") ? (
              <a href={contactHref} className="hover:underline" style={{ color: ink }}>
                Contact
              </a>
            ) : (
              <Link to={contactHref} className="hover:underline" style={{ color: ink }}>
                Contact
              </Link>
            )}
            {isAhp && flags.showReleaseNotes && (
              <Link to="/release-notes" className="hover:underline" style={{ color: ink }}>
                What's new
              </Link>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
